import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Card, Row, Col, Nav, Tab, Form, Button, Modal, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { adminHoldSchema } from '../../../schemas/wallet/admin.schema';
import { Plus } from 'lucide-react';
import DataTable from '../../../component/wallet/data-table/DataTable';
import DataTableToolbar from '../../../component/wallet/data-table/DataTableToolbar';
import StatusBadge from '../../../component/wallet/StatusBadge';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import ConfirmDialog from '../../../component/wallet/ConfirmDialog';
import LoadingSpinner from '../../../component/wallet/LoadingSpinner';
import { useAdmin } from '../../../Hooks/wallet/useadmin';
import { formatDate } from '../../../lib/wallet/utils';
import { TRANSACTION_TYPES, DEFAULT_PAGE_SIZE } from '../../../lib/wallet/constants';

const columnHelper = createColumnHelper();

const UserDetailPage = () => {
    const { sidebar } = useContext(AppContext);
    const { userId } = useParams();
    const {
        loading,
        fetchUserWallets,
        fetchUserTransactions,
        fetchHolds,
        doCreateHold,
        doCaptureHold,
        doReleaseHold,
    } = useAdmin();

    // Wallets + user info
    const [wallets, setWallets] = useState([]);
    const [userInfo, setUserInfo] = useState(null);

    // Transactions
    const [transactions, setTransactions] = useState([]);
    const [typeFilter, setTypeFilter] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [txnPagination, setTxnPagination] = useState({ page: 1, limit: DEFAULT_PAGE_SIZE, total_count: 0, total_pages: 1 });

    // Holds
    const [userHolds, setUserHolds] = useState([]);
    const [holdsPagination, setHoldsPagination] = useState({ page: 1, limit: DEFAULT_PAGE_SIZE, total_count: 0, total_pages: 1 });
    const [showCreateHold, setShowCreateHold] = useState(false);

    const {
        register: holdRegister,
        handleSubmit: holdHandleSubmit,
        reset: holdReset,
        formState: { errors: holdErrors },
    } = useForm({ resolver: zodResolver(adminHoldSchema) });

    // ── Initial load: wallets + first page of holds ─────────────────
    useEffect(() => {
        const load = async () => {
            try {
                const [wRes, hRes] = await Promise.all([
                    fetchUserWallets(userId),
                    fetchHolds({ user_id: userId, limit: DEFAULT_PAGE_SIZE }),
                ]);
                const w = wRes?.data || [];
                setWallets(w);
                if (w.length > 0) {
                    setUserInfo({
                        name: w[0].full_name,
                        email: w[0].email,
                        external_id: w[0].external_user_id,
                    });
                }
                setUserHolds(hRes?.data?.holds || []);
                setHoldsPagination((prev) => ({ ...prev, ...hRes?.data?.pagination }));
            } catch {
                // handled
            }
        };
        load();
    }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Transactions ────────────────────────────────────────────────
    const loadTransactions = useCallback(
        async (page = 1) => {
            try {
                const res = await fetchUserTransactions(userId, {
                    page,
                    limit: txnPagination.limit,
                    type: typeFilter || undefined,
                    from_date: fromDate || undefined,
                    to_date: toDate || undefined,
                });
                setTransactions(res?.data?.transactions || []);
                setTxnPagination((prev) => ({ ...prev, page, ...res?.data?.pagination }));
            } catch {
                // handled
            }
        },
        [userId, txnPagination.limit, typeFilter, fromDate, toDate, fetchUserTransactions]
    );

    useEffect(() => {
        loadTransactions(1);
    }, [typeFilter, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Holds ───────────────────────────────────────────────────────
    const loadUserHolds = useCallback(async (page = 1) => {
        try {
            const res = await fetchHolds({ user_id: userId, page, limit: holdsPagination.limit });
            setUserHolds(res?.data?.holds || []);
            setHoldsPagination((prev) => ({ ...prev, page, ...res?.data?.pagination }));
        } catch {
            // handled
        }
    }, [fetchHolds, userId, holdsPagination.limit]);

    const onCreateHold = async (data) => {
        try {
            await doCreateHold(data);
            setShowCreateHold(false);
            holdReset();
            loadUserHolds(1);
        } catch {
            // handled
        }
    };

    const handleCapture = useCallback(async (holdId) => {
        const confirmed = await ConfirmDialog({
            title: 'Capture Hold',
            message: `Capture hold #${holdId}? This will deduct the amount from the wallet balance.`,
        });
        if (!confirmed) return;
        try {
            await doCaptureHold({ hold_id: holdId });
            setUserHolds((prev) => prev.map((h) => h.hold_id === holdId ? { ...h, status: 'captured' } : h));
        } catch {
            // handled
        }
    }, [doCaptureHold]);

    const handleRelease = useCallback(async (holdId) => {
        const confirmed = await ConfirmDialog({
            title: 'Release Hold',
            message: `Release hold #${holdId}? This will free the reserved funds.`,
        });
        if (!confirmed) return;
        try {
            await doReleaseHold({ hold_id: holdId });
            setUserHolds((prev) => prev.map((h) => h.hold_id === holdId ? { ...h, status: 'released' } : h));
        } catch {
            // handled
        }
    }, [doReleaseHold]);

    // ── Columns ─────────────────────────────────────────────────────
    const walletColumns = useMemo(
        () => [
            columnHelper.accessor('wallet_id', { header: 'ID', size: 60 }),
            columnHelper.accessor('currency_code', { header: 'Currency' }),
            columnHelper.accessor('wallet_type', {
                header: 'Type',
                cell: (info) => <StatusBadge status={info.getValue()} />,
            }),
            columnHelper.accessor('balance', {
                header: 'Balance',
                cell: (info) => (
                    <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
                ),
            }),
            columnHelper.accessor('available_balance', {
                header: 'Available',
                cell: (info) => (
                    <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
                ),
            }),
            columnHelper.accessor('held_amount', {
                header: 'Held',
                cell: (info) => (
                    <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
                ),
            }),
        ],
        []
    );

    const txnColumns = useMemo(
        () => [
            columnHelper.accessor('id', { header: 'ID', size: 60 }),
            columnHelper.accessor('type', {
                header: 'Type',
                cell: (info) => <StatusBadge status={info.getValue()} />,
            }),
            columnHelper.accessor('amount', {
                header: 'Amount',
                cell: (info) => (
                    <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
                ),
            }),
            columnHelper.display({
                id: 'balance',
                header: 'Balance',
                cell: ({ row }) => (
                    <span className="small">
                        {Number(row.original.balance_before).toFixed(2)} → {Number(row.original.balance_after).toFixed(2)}
                    </span>
                ),
            }),
            columnHelper.accessor('description', { header: 'Description', cell: (info) => info.getValue() || '—' }),
            columnHelper.accessor('created_at', {
                header: 'Date',
                cell: (info) => formatDate(info.getValue()),
            }),
        ],
        []
    );

    const holdColumns = useMemo(
        () => [
            columnHelper.accessor('hold_id', { header: 'ID', size: 60 }),
            columnHelper.accessor('wallet_id', { header: 'Wallet ID', size: 80 }),
            columnHelper.accessor('amount', {
                header: 'Amount',
                cell: (info) => (
                    <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
                ),
            }),
            columnHelper.accessor('wallet_type', {
                header: 'Type',
                cell: (info) => <StatusBadge status={info.getValue()} />,
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: (info) => {
                    const status = info.getValue();
                    const expired = info.row.original.effectively_expired;
                    return (
                        <span className="d-flex align-items-center gap-1">
                            <StatusBadge status={status} />
                            {expired && status === 'active' && (
                                <span className="badge bg-secondary">Expired</span>
                            )}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('reference_id', {
                header: 'Reference',
                cell: (info) => {
                    const refId = info.getValue();
                    const refType = info.row.original.reference_type;
                    if (!refId) return '—';
                    return refType ? `${refType}: ${refId}` : refId;
                },
            }),
            columnHelper.accessor('expires_at', {
                header: 'Expires',
                cell: (info) => (info.getValue() ? formatDate(info.getValue(), 'MMM dd, yyyy') : '—'),
            }),
            columnHelper.accessor('created_at', {
                header: 'Created',
                cell: (info) => formatDate(info.getValue()),
            }),
            columnHelper.display({
                id: 'actions',
                header: 'Actions',
                cell: ({ row }) => {
                    const { hold_id, status, effectively_expired } = row.original;
                    const canAct = status === 'active' && !effectively_expired;
                    return canAct ? (
                        <div className="d-flex gap-1">
                            <Button size="sm" variant="outline-success" onClick={() => handleCapture(hold_id)}>
                                Capture
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => handleRelease(hold_id)}>
                                Release
                            </Button>
                        </div>
                    ) : '—';
                },
            }),
        ],
        [handleCapture, handleRelease]
    );

    if (loading && wallets.length === 0) return <LoadingSpinner fullPage />;

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">User Detail</h6>
            </div>
                <div className="fade-in">
            

            {userInfo && (
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body>
                        <Row>
                            <Col sm={4}><strong>Name:</strong> {userInfo.name}</Col>
                            <Col sm={4}><strong>Email:</strong> {userInfo.email}</Col>
                            <Col sm={4}><strong>External ID:</strong> {userInfo.external_id}</Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            <Tab.Container defaultActiveKey="wallets">
                <Nav variant="tabs" className="mb-3">
                    <Nav.Item><Nav.Link eventKey="wallets">Wallets</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="transactions">Transactions</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="holds">Holds</Nav.Link></Nav.Item>
                </Nav>

                <Tab.Content>
                    {/* ── Wallets ── */}
                    <Tab.Pane eventKey="wallets">
                        <DataTable
                            columns={walletColumns}
                            data={wallets}
                            loading={loading}
                            emptyTitle="No wallets"
                            emptyDescription="This user has no wallets."
                        />
                    </Tab.Pane>

                    {/* ── Transactions ── */}
                    <Tab.Pane eventKey="transactions">
                        <DataTableToolbar
                            filters={[
                                {
                                    value: typeFilter,
                                    onChange: setTypeFilter,
                                    placeholder: 'All Types',
                                    options: TRANSACTION_TYPES,
                                },
                            ]}
                        >
                            <Col xs={6} md={2}>
                                <Form.Control size="sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                            </Col>
                            <Col xs={6} md={2}>
                                <Form.Control size="sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                            </Col>
                        </DataTableToolbar>
                        <DataTable
                            columns={txnColumns}
                            data={transactions}
                            loading={loading}
                            page={txnPagination.page}
                            pageSize={txnPagination.limit}
                            totalCount={txnPagination.total_count}
                            totalPages={txnPagination.total_pages}
                            onPageChange={(p) => loadTransactions(p)}
                            onPageSizeChange={(s) => setTxnPagination((prev) => ({ ...prev, limit: s }))}
                            emptyTitle="No transactions"
                            emptyDescription="No transactions found for this user."
                        />
                    </Tab.Pane>

                    {/* ── Holds ── */}
                    <Tab.Pane eventKey="holds">
                        <div className="d-flex justify-content-end mb-3">
                            <Button variant="primary" size="sm" onClick={() => setShowCreateHold(true)}>
                                <Plus size={14} className="me-1" />
                                Create Hold
                            </Button>
                        </div>
                        <DataTable
                            columns={holdColumns}
                            data={userHolds}
                            loading={loading}
                            page={holdsPagination.page}
                            pageSize={holdsPagination.limit}
                            totalCount={holdsPagination.total_count}
                            totalPages={holdsPagination.total_pages}
                            onPageChange={(p) => loadUserHolds(p)}
                            onPageSizeChange={(s) => setHoldsPagination((prev) => ({ ...prev, limit: s }))}
                            emptyTitle="No holds"
                            emptyDescription="This user has no holds."
                        />
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            {/* Create Hold Modal */}
            <Modal show={showCreateHold} onHide={() => { setShowCreateHold(false); holdReset(); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Create Hold</Modal.Title>
                </Modal.Header>
                <Form onSubmit={holdHandleSubmit(onCreateHold)}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Wallet</Form.Label>
                            <Form.Select isInvalid={!!holdErrors.wallet_id} {...holdRegister('wallet_id')}>
                                <option value="">Select wallet</option>
                                {wallets.map((w) => (
                                    <option key={w.wallet_id} value={w.wallet_id}>
                                        #{w.wallet_id} — {w.currency_symbol}{w.currency_code} ({w.wallet_type})
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{holdErrors.wallet_id?.message}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Amount</Form.Label>
                            <Form.Control type="number" step="0.01" isInvalid={!!holdErrors.amount} {...holdRegister('amount')} />
                            <Form.Control.Feedback type="invalid">{holdErrors.amount?.message}</Form.Control.Feedback>
                        </Form.Group>
                        <Row className="g-2 mb-3">
                            <Col>
                                <Form.Group>
                                    <Form.Label>Reference ID <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                    <Form.Control type="text" {...holdRegister('reference_id')} />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group>
                                    <Form.Label>Reference Type <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                    <Form.Control type="text" {...holdRegister('reference_type')} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group>
                            <Form.Label>Expires At <span className="text-muted fw-normal">(optional)</span></Form.Label>
                            <Form.Control type="datetime-local" {...holdRegister('expires_at')} />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => { setShowCreateHold(false); holdReset(); }}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Create Hold'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
        </div>
        </div>
    );
};

export default UserDetailPage;
